import React from 'react';
import { FileText, ShoppingBag, CreditCard, Download, Clock } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { ZodiacTheme } from './zodiacThemes';
import { User } from '../../types';
interface OverviewTabProps {
  theme: ZodiacTheme;
  user: User | null;
  setActiveTab: (tab: string) => void;
}
export function OverviewTab({
  theme,
  user,
  setActiveTab
}: OverviewTabProps) {
  return <div className="space-y-6" data-id="element-15728">
      {/* Themed Welcome Banner */}
      <div className={`rounded-3xl bg-gradient-to-r ${theme.gradient} p-8 text-white shadow-lg relative overflow-hidden`} data-id="element-15729">
        <div className="absolute top-0 right-0 -mt-10 -mr-10 text-[180px] opacity-10 pointer-events-none leading-none" data-id="element-15730">
          {theme.icon}
        </div>
        <div className="relative z-10" data-id="element-15731">
          <div className="flex items-center gap-2 mb-2" data-id="element-15732">
            <span className="bg-white/20 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider backdrop-blur-sm" data-id="element-15733">
              {theme.name} • {theme.element}
            </span>
          </div>
          <h2 className="text-3xl font-bold mb-2" data-id="element-15734">
            Добро пожаловать в вашу вселенную, {user?.name || 'Гость'}!
          </h2>
          <p className="text-white/80 max-w-xl" data-id="element-15735">
            Ваша натальная карта активна. Сегодня Луна в гармоничном аспекте с
            вашим Солнцем — отличный день для новых начинаний.
          </p>
          <div className="mt-6 flex gap-3" data-id="element-15736">
            <Button variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20" onClick={() => setActiveTab('calendar')} data-id="element-15737">
              Открыть календарь
            </Button>
            <Button variant="outline" className="bg-white text-slate-900 border-none hover:bg-slate-100" onClick={() => setActiveTab('profile')} data-id="element-15738">
              Мой профиль
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Widgets */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-15739">
        <Card padding="md" className="flex items-center hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveTab('reports')} data-id="element-15740">
          <div className={`w-12 h-12 rounded-full ${theme.bg} ${theme.text} flex items-center justify-center mr-4`} data-id="element-15741">
            <FileText className="w-6 h-6" data-id="element-15742" />
          </div>
          <div data-id="element-15743">
            <div className="text-sm text-slate-500 font-medium" data-id="element-15744">
              Готовых отчётов
            </div>
            <div className="text-2xl font-bold text-slate-900" data-id="element-15745">3</div>
          </div>
        </Card>
        <Card padding="md" className="flex items-center hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveTab('credits')} data-id="element-15746">
          <div className={`w-12 h-12 rounded-full ${theme.bg} ${theme.text} flex items-center justify-center mr-4`} data-id="element-15747">
            <CreditCard className="w-6 h-6" data-id="element-15748" />
          </div>
          <div data-id="element-15749">
            <div className="text-sm text-slate-500 font-medium" data-id="element-15750">
              Доступно кредитов
            </div>
            <div className="text-2xl font-bold text-slate-900" data-id="element-15751">2</div>
          </div>
        </Card>
        <Card padding="md" className="flex items-center hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveTab('orders')} data-id="element-15752">
          <div className={`w-12 h-12 rounded-full ${theme.bg} ${theme.text} flex items-center justify-center mr-4`} data-id="element-15753">
            <ShoppingBag className="w-6 h-6" data-id="element-15754" />
          </div>
          <div data-id="element-15755">
            <div className="text-sm text-slate-500 font-medium" data-id="element-15756">
              Активных заказов
            </div>
            <div className="text-2xl font-bold text-slate-900" data-id="element-15757">1</div>
          </div>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8" data-id="element-15758">
        <Card padding="lg" className={`border-t-4 ${theme.border.replace('border-', 'border-t-')}`} data-id="element-15759">
          <div className="flex justify-between items-center mb-6" data-id="element-15760">
            <h3 className="font-bold text-slate-900" data-id="element-15761">Последние отчёты</h3>
            <button onClick={() => setActiveTab('reports')} className={`text-sm ${theme.text} hover:underline`} data-id="element-15762">
              Все
            </button>
          </div>
          <div className="space-y-4" data-id="element-15763">
            {[1, 2].map(i => <div key={i} className="flex items-center justify-between p-4 border border-slate-100 rounded-xl hover:border-slate-200 transition-colors" data-id="element-15764">
                <div className="flex items-center" data-id="element-15765">
                  <div className={`w-10 h-10 rounded-lg ${theme.bg} flex items-center justify-center mr-4`} data-id="element-15766">
                    <FileText className={`w-5 h-5 ${theme.text}`} data-id="element-15767" />
                  </div>
                  <div data-id="element-15768">
                    <div className="font-medium text-slate-900" data-id="element-15769">
                      {i === 1 ? 'Натальная карта' : 'Соляр 2024'}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-15770">
                      12 Октября 2024
                    </div>
                  </div>
                </div>
                <Button variant="ghost" size="sm" icon={<Download className="w-4 h-4" data-id="element-15772" />} data-id="element-15771">
                  PDF
                </Button>
              </div>)}
          </div>
        </Card>

        <Card padding="lg" data-id="element-15773">
          <div className="flex justify-between items-center mb-6" data-id="element-15774">
            <h3 className="font-bold text-slate-900" data-id="element-15775">Текущий заказ</h3>
            <button onClick={() => setActiveTab('orders')} className={`text-sm ${theme.text} hover:underline`} data-id="element-15776">
              Все
            </button>
          </div>
          <div className="border border-slate-100 rounded-xl p-5" data-id="element-15777">
            <div className="flex justify-between items-start mb-4" data-id="element-15778">
              <div data-id="element-15779">
                <Badge variant="warning" className="mb-2" data-id="element-15780">
                  В работе
                </Badge>
                <div className="font-bold text-slate-900" data-id="element-15781">
                  Консультация: Синастрия
                </div>
              </div>
              <div className="text-right" data-id="element-15782">
                <div className="text-sm font-medium text-slate-900" data-id="element-15783">
                  Елена Смирнова
                </div>
                <div className="text-xs text-slate-500" data-id="element-15784">Видеозвонок</div>
              </div>
            </div>
            <div className="bg-slate-50 p-3 rounded-lg flex items-center text-sm text-slate-600 mb-4" data-id="element-15785">
              <Clock className="w-4 h-4 mr-2 text-amber-500" data-id="element-15786" /> Ожидайте ссылку
              на звонок 15 Октября в 14:00
            </div>
            <Button variant="outline" fullWidth onClick={() => setActiveTab('chat')} data-id="element-15787">
              Открыть чат
            </Button>
          </div>
        </Card>
      </div>
    </div>;
}